import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { isCapable } from '../structureLogic';
import { useRenameField } from '../hooks/useStructureQueries';

interface RenameFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldId: string;
  currentName: string;
  /** Существующие значения FieldIn — PATCH требует полный объект. */
  fieldPayloadBase: Record<string, unknown>;
  onRenamed?: (newName: string) => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Переименование поля (AK-346). Пишет новое имя через PATCH /fields/{id}.
 *
 * Event sourcing (история имён + «действует с» датой начала действия) —
 * решение founder, но бэк дорабатывается (AK-344): поля «Действует с» и
 * таймлайн истории имён показываются только при capable('fieldNameHistory') /
 * capable('renameFieldEffectiveDate'). Пока — простое переименование.
 */
export function RenameFieldDialog({
  open,
  onOpenChange,
  fieldId,
  currentName,
  fieldPayloadBase,
  onRenamed,
}: RenameFieldDialogProps) {
  const rename = useRenameField(fieldId);
  const [name, setName] = useState(currentName);
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | undefined>();

  const showEffectiveDate = isCapable('renameFieldEffectiveDate');

  useEffect(() => {
    if (open) {
      setName(currentName);
      setEffectiveFrom(today());
      setReason('');
      setError(undefined);
    }
  }, [open, currentName]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Укажите новое имя');
      return;
    }
    if (trimmed === currentName.trim()) {
      onOpenChange(false);
      return;
    }
    try {
      // PATCH partial (#391): достаточно name; name_valid_from = «дата начала
      // нового имени» (founder AK-344) — шлём, если пользователь её менял.
      const effective =
        showEffectiveDate && effectiveFrom && effectiveFrom !== today()
          ? { name_valid_from: new Date(`${effectiveFrom}T00:00:00`).toISOString() }
          : {};
      await rename.mutateAsync({ ...fieldPayloadBase, name: trimmed, ...effective });
      toast.success(`Поле переименовано в «${trimmed}»`);
      onRenamed?.(trimmed);
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) setError(e.detail);
      else setError('Не удалось переименовать поле');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Переименовать поле</DialogTitle>
          <DialogDescription>
            {showEffectiveDate
              ? 'Старое имя сохранится в истории. На картах и в отчётах за прошлые периоды останется имя, актуальное на ту дату.'
              : 'Изменит отображаемое имя поля во всех списках и на карте.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Текущее имя</Label>
            <div className="flex h-10 items-center rounded-md border border-input bg-secondary/30 px-3 text-sm text-muted-foreground">
              {currentName}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rename-new">
              Новое имя <span className="text-destructive">*</span>
            </Label>
            <Input
              id="rename-new"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(undefined);
              }}
              aria-invalid={!!error}
            />
          </div>

          {showEffectiveDate && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="rename-from">
                  Действует с <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="rename-from"
                  type="date"
                  value={effectiveFrom}
                  onChange={(e) => setEffectiveFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="rename-reason">
                  Причина{' '}
                  <span className="font-normal text-muted-foreground">· необяз.</span>
                </Label>
                <Input
                  id="rename-reason"
                  placeholder="разделение поля"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </div>
          )}

          {!showEffectiveDate && (
            <div className="flex gap-2 rounded-md bg-accent-brand/10 p-3 text-xs text-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent-brand" />
              <span>
                История имён с датами действия («действует с», отчёты за прошлое)
                — скоро, бэкенд дорабатывается.
              </span>
            </div>
          )}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={rename.isPending}
          >
            Отмена
          </Button>
          <Button
            onClick={submit}
            disabled={rename.isPending}
          >
            {rename.isPending ? 'Сохраняем…' : 'Сохранить новое имя'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
